// Mock data for offline / demo mode. Activated by REACT_APP_MOCK_MODE=true.
// Lets reviewers click through every page without a Cognito session or live
// backend. Intentionally hand-tuned to exercise the UI states (at-risk students,
// in-progress cohort, qualified students, PLA banner, etc.).

export const MOCK_USER = {
  email: 'demo.coordinator@gcu.edu',
  groups: ['college_admin'],
  role: 'college_admin',
  sub: 'demo-user-1',
  name: 'Dr. Maria Lopez',
  college_id: 'gcu-001',
  college_name: 'Grand Canyon University',
  college_logo_url: '/images/college-gcu.png',
  cohort_id: null,
  // Toggle to false to verify Billing nav disappears + the friendly access-
  // denied banner appears at /billing.
  is_billing_admin: true,
};

const MOCK_COLLEGE_ID = 'gcu-001';

// Credit requirements: a single `assessments` count covers both the hands-on
// assessment AND the mandatory mentor review (every assessment includes a
// mentor approval — they're never separate counts).
const MOCK_COHORTS = [
  {
    cohort_id: 'gcu-001-fall-2026',
    college_id: MOCK_COLLEGE_ID,
    name: 'Fall 2026',
    start_date: '2026-08-25',
    end_date: '2026-12-15',
    course_code: 'CYB-480',
    status: 'ACTIVE',
    enrolled_count: 14,
    enrollment_link_code: 'gcu-fall-2026-x9k2',
    credit_requirements: { hours: 60, assessments: 6 },
    created_at: '2026-04-12T15:30:00Z',
  },
  {
    cohort_id: 'gcu-001-summer-2026',
    college_id: MOCK_COLLEGE_ID,
    name: 'Summer 2026',
    start_date: '2026-05-15',
    end_date: '2026-08-10',
    course_code: 'CYB-490',
    status: 'ACTIVE',
    enrolled_count: 8,
    enrollment_link_code: 'gcu-summer-2026-m7p4',
    credit_requirements: { hours: 120, assessments: 10 },
    created_at: '2026-03-01T11:00:00Z',
  },
  {
    cohort_id: 'gcu-001-spring-2026',
    college_id: MOCK_COLLEGE_ID,
    name: 'Spring 2026',
    start_date: '2026-01-10',
    end_date: '2026-05-05',
    course_code: 'CYB-480',
    status: 'COMPLETED',
    enrolled_count: 11,
    enrollment_link_code: null,
    credit_requirements: { hours: 60, assessments: 6 },
    created_at: '2025-12-15T09:00:00Z',
  },
];

const STUDENT_NAMES = [
  ['Alex', 'Chen'], ['Jordan', 'Williams'], ['Taylor', 'Patel'], ['Sam', 'Rodriguez'],
  ['Riley', 'Kim'], ['Casey', 'Nguyen'], ['Morgan', 'Davis'], ['Jamie', 'Thompson'],
  ['Drew', 'Garcia'], ['Avery', 'Martinez'], ['Quinn', 'Lopez'], ['Hayden', 'Singh'],
  ['Reese', 'Brown'], ['Emery', 'Wilson'], ['Skylar', 'Johnson'], ['Parker', 'Anderson'],
  ['Logan', 'Hernandez'], ['Cameron', 'Lee'], ['Rowan', 'Khan'], ['Sage', 'Walker'],
];

const MENTORS = ['Robert Hibler', 'Chad Freese', 'Caleb King', 'Chelsea Stevenson', 'Andrew Wise'];

function makeStudent(idx, cohort) {
  const [first, last] = STUDENT_NAMES[idx % STUDENT_NAMES.length];
  const reqs = cohort.credit_requirements;
  // Spread progress: ~25% at-risk, ~10% qualified, ~10% withdrawn, rest on-track
  const r = Math.random();
  let assessmentsCompleted, hoursLogged, status;
  if (r < 0.10) {
    // Withdrawn
    assessmentsCompleted = Math.floor(reqs.assessments * 0.3);
    hoursLogged = Math.floor(reqs.hours * 0.3);
    status = 'withdrawn';
  } else if (r < 0.20) {
    // Qualified
    assessmentsCompleted = reqs.assessments;
    hoursLogged = reqs.hours + Math.floor(Math.random() * 10);
    status = 'qualified';
  } else if (r < 0.45) {
    // At-risk
    const pct = 0.1 + Math.random() * 0.3;
    assessmentsCompleted = Math.floor(reqs.assessments * pct);
    hoursLogged = Math.floor(reqs.hours * pct);
    status = 'at_risk';
  } else {
    // On-track
    const pct = 0.5 + Math.random() * 0.4;
    assessmentsCompleted = Math.floor(reqs.assessments * pct);
    hoursLogged = Math.floor(reqs.hours * pct);
    status = 'on_track';
  }
  const progressPct = Math.min(100, Math.round((assessmentsCompleted / Math.max(1, reqs.assessments)) * 100));
  return {
    student_id: `student-${cohort.cohort_id}-${idx}`,
    enrollment_id: `enroll-${cohort.cohort_id}-${idx}`,
    name: `${first} ${last}`,
    email: `${first.toLowerCase()}.${last.toLowerCase()}@my.gcu.edu`,
    cohort_id: cohort.cohort_id,
    cohort_name: cohort.name,
    course_code: cohort.course_code,
    mentor_id: `mentor-${idx % MENTORS.length}`,
    mentor_name: MENTORS[idx % MENTORS.length],
    hours_logged: hoursLogged,
    hours_required: reqs.hours,
    assessments_completed: assessmentsCompleted,
    assessments_required: reqs.assessments,
    progress_pct: progressPct,
    status,
    enrolled_at: cohort.created_at,
    pla_credits: idx % 7 === 0 ? 15 : 0,
  };
}

// Stable seed — fixed students per cohort so the mock dataset is deterministic
// across page navigations.
const MOCK_STUDENTS = (() => {
  // Use seeded rng for deterministic data
  let seed = 42;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const origRandom = Math.random;
  Math.random = rand;
  const all = [];
  MOCK_COHORTS.forEach((c) => {
    for (let i = 0; i < c.enrolled_count; i++) all.push(makeStudent(all.length, c));
  });
  Math.random = origRandom;
  return all;
})();

const MOCK_RECENT_ACTIVITY = [
  { id: 1, type: 'mentor_approval', message: 'Mentor approval: Alex Chen passed home assessment review', actor: 'Robert Hibler', timestamp: new Date(Date.now() - 1000 * 60 * 18).toISOString() },
  { id: 2, type: 'assessment_completed', message: 'Assessment completed: Jordan Williams · home', actor: 'Jordan Williams', timestamp: new Date(Date.now() - 1000 * 60 * 47).toISOString() },
  { id: 3, type: 'enrollment', message: 'New enrollment: Sage Walker joined Fall 2026', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString() },
  { id: 4, type: 'mentor_approval', message: 'Mentor approval: Riley Kim passed business assessment review', actor: 'Chad Freese', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString() },
  { id: 5, type: 'cohort_created', message: 'Cohort created: Summer 2026', actor: 'Dr. Maria Lopez', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 22).toISOString() },
  { id: 6, type: 'assessment_completed', message: 'Assessment completed: Casey Nguyen · home', actor: 'Casey Nguyen', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 28).toISOString() },
  { id: 7, type: 'transcript_generated', message: 'Transcript generated for Hayden Singh', actor: 'Dr. Maria Lopez', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString() },
  { id: 8, type: 'mentor_approval', message: 'Mentor approval: Drew Garcia passed home assessment review', actor: 'Caleb King', timestamp: new Date(Date.now() - 1000 * 60 * 60 * 56).toISOString() },
];

const MOCK_INSTITUTION = {
  college_id: MOCK_COLLEGE_ID,
  name: 'Grand Canyon University',
  domain: 'gcu.edu',
  contact_name: 'Dr. Maria Lopez',
  contact_email: 'maria.lopez@gcu.edu',
  contact_phone: '(602) 555-0142',
  billing_tier: 'per_student',
  per_student_fee_cents: 7500,
  credit_defaults: { hours: 60, assessments: 6 },
  insurance_certificate: {
    url: 'https://example.com/gcu-insurance.pdf',
    uploaded_at: '2026-02-14T10:30:00Z',
    expires_at: '2027-02-14T10:30:00Z',
  },
};

// Audit log of material settings changes. Newest first.
const MOCK_CHANGELOG = [
  {
    id: 'chg-3',
    timestamp: '2026-04-10T14:32:00Z',
    actor_name: 'Dr. Maria Lopez',
    actor_email: 'maria.lopez@gcu.edu',
    field: 'assessments_required',
    field_label: 'Assessments Required (default)',
    old_value: 5,
    new_value: 6,
    reason: 'Aligning with updated CYB-480 syllabus requirements approved by department chair on 4/8.',
    affected_students: 14,
    became_at_risk: 2,
  },
  {
    id: 'chg-2',
    timestamp: '2026-03-22T09:15:00Z',
    actor_name: 'Dr. Maria Lopez',
    actor_email: 'maria.lopez@gcu.edu',
    field: 'hours_required',
    field_label: 'Hours Required (default)',
    old_value: 50,
    new_value: 60,
    reason: 'ABET accreditor feedback: minimum 60 hours of supervised field work per practicum credit.',
    affected_students: 22,
    became_at_risk: 4,
  },
  {
    id: 'chg-1',
    timestamp: '2026-01-08T16:48:00Z',
    actor_name: 'Dr. Maria Lopez',
    actor_email: 'maria.lopez@gcu.edu',
    field: 'assessments_required',
    field_label: 'Assessments Required (default)',
    old_value: 4,
    new_value: 5,
    reason: 'Pilot semester learnings — students need more reps to build interview confidence.',
    affected_students: 11,
    became_at_risk: 0,
  },
];

// IDs of cohorts whose students are still affected by the most-recent change
// (i.e. enrolled before 2026-04-10 and not yet qualified).
const RECENT_CHANGE = MOCK_CHANGELOG[0];

// ─── Billing ─────────────────────────────────────────────────────────────────
// Mocked subscription state. Real backend would join Stripe customer + per-
// student headcount + invoice records.
const MOCK_BILLING_PLAN = {
  plan_id: 'institutional_flat',
  plan_name: 'Institutional — Flat Rate',
  per_student_rate_cents: 7500, // $75.00
  billing_cadence: 'per_semester',
  currency: 'usd',
  payment_method: {
    type: 'card',
    brand: 'visa',
    last4: '4242',
    exp_month: 11,
    exp_year: 2028,
    cardholder_name: 'Grand Canyon University',
  },
  // Set to a payment_method object with type='invoice' to switch the UI to a PO/Invoice flow:
  // payment_method: { type: 'invoice', po_number: 'PO-2026-0481', terms: 'Net 30' },
  stripe_customer_id: 'cus_mock_GCU001',
  billing_email: 'finance@gcu.edu',
};

// Helper: compute the active billing period from the active cohort dates.
function getBillingPeriod() {
  const activeCohorts = MOCK_COHORTS.filter((c) => c.status === 'ACTIVE');
  const earliest = activeCohorts.reduce((min, c) =>
    !min || c.start_date < min ? c.start_date : min, null);
  const latest = activeCohorts.reduce((max, c) =>
    !max || c.end_date > max ? c.end_date : max, null);
  return { start_date: earliest, end_date: latest, label: 'Fall 2026 + Summer 2026' };
}

const MOCK_INVOICES = [
  {
    invoice_id: 'inv_2026_fall',
    invoice_number: 'CWC-2026-0042',
    issued_at: '2026-04-01T00:00:00Z',
    due_at: '2026-05-01T00:00:00Z',
    period_label: 'Fall 2026 + Summer 2026',
    students_billed: 22,
    rate_cents: 7500,
    amount_cents: 22 * 7500,
    status: 'pending',
    pdf_url: null,
  },
  {
    invoice_id: 'inv_2026_spring',
    invoice_number: 'CWC-2026-0017',
    issued_at: '2026-01-15T00:00:00Z',
    due_at: '2026-02-14T00:00:00Z',
    paid_at: '2026-01-22T14:18:00Z',
    period_label: 'Spring 2026',
    students_billed: 11,
    rate_cents: 7500,
    amount_cents: 11 * 7500,
    status: 'paid',
    pdf_url: 'mock://invoice-spring-2026.pdf',
  },
  {
    invoice_id: 'inv_2025_fall',
    invoice_number: 'CWC-2025-0083',
    issued_at: '2025-08-15T00:00:00Z',
    due_at: '2025-09-14T00:00:00Z',
    paid_at: '2025-08-28T11:02:00Z',
    period_label: 'Fall 2025',
    students_billed: 9,
    rate_cents: 7500,
    amount_cents: 9 * 7500,
    status: 'paid',
    pdf_url: 'mock://invoice-fall-2025.pdf',
  },
  {
    invoice_id: 'inv_2025_spring',
    invoice_number: 'CWC-2025-0021',
    issued_at: '2025-01-15T00:00:00Z',
    due_at: '2025-02-14T00:00:00Z',
    paid_at: '2025-02-03T09:45:00Z',
    period_label: 'Spring 2025 (pilot)',
    students_billed: 5,
    rate_cents: 7500,
    amount_cents: 5 * 7500,
    status: 'paid',
    pdf_url: 'mock://invoice-spring-2025.pdf',
  },
];

const MOCK_COURSE_CODES = [
  { course_code: 'CYB-480', description: 'Cybersecurity Practicum I', hours_required: 60 },
  { course_code: 'CYB-490', description: 'Cybersecurity Capstone', hours_required: 120 },
  { course_code: 'CYB-485', description: 'Independent Study', hours_required: 45 },
];

const MOCK_OBSERVERS = [
  { email: 'james.holden@gcu.edu', name: 'Dr. James Holden', role: 'faculty_advisor', status: 'active' },
  { email: 'naomi.nagata@gcu.edu', name: 'Naomi Nagata', role: 'faculty_advisor', status: 'active' },
  { email: 'dean.cybersec@gcu.edu', name: 'Dean Avasarala', role: 'college_observer', status: 'pending' },
];

// ─── Dispatch ────────────────────────────────────────────────────────────────
const delay = (ms = 250) => new Promise((res) => setTimeout(res, ms));

export async function mockApiResponse(action, params) {
  await delay();

  switch (action) {
    case 'get_dashboard_stats': {
      const active = MOCK_STUDENTS.filter((s) => MOCK_COHORTS.find((c) => c.cohort_id === s.cohort_id)?.status === 'ACTIVE');
      const onTrack = active.filter((s) => s.status === 'on_track' || s.status === 'qualified').length;
      const atRisk = active.filter((s) => s.status === 'at_risk').length;
      const completed = MOCK_STUDENTS.reduce((acc, s) => acc + s.assessments_completed, 0);
      return {
        active_students: active.length,
        active_cohorts: MOCK_COHORTS.filter((c) => c.status === 'ACTIVE').length,
        on_track_count: onTrack,
        at_risk_count: atRisk,
        assessments_completed: completed,
        assessments_this_semester: Math.floor(completed * 0.7),
      };
    }

    case 'get_recent_activity':
      return { activity: MOCK_RECENT_ACTIVITY.slice(0, params.limit || 8) };

    case 'get_at_risk_students': {
      const atRisk = MOCK_STUDENTS.filter((s) => s.status === 'at_risk').slice(0, params.limit || 5);
      return { students: atRisk };
    }

    case 'list_cohorts':
      return { cohorts: MOCK_COHORTS.filter((c) => !params.status || c.status === params.status) };

    case 'get_cohort':
      return MOCK_COHORTS.find((c) => c.cohort_id === params.cohort_id) || null;

    case 'create_cohort': {
      const newCohort = {
        cohort_id: `gcu-001-${(params.name || 'new').toLowerCase().replace(/\s+/g, '-')}`,
        college_id: MOCK_COLLEGE_ID,
        ...params,
        enrolled_count: 0,
        status: 'ACTIVE',
        enrollment_link_code: `${Math.random().toString(36).slice(2, 10)}`,
        created_at: new Date().toISOString(),
      };
      MOCK_COHORTS.unshift(newCohort);
      return newCohort;
    }

    case 'update_cohort': {
      const c = MOCK_COHORTS.find((c) => c.cohort_id === params.cohort_id);
      if (c) Object.assign(c, params);
      return c;
    }

    case 'end_cohort': {
      const c = MOCK_COHORTS.find((c) => c.cohort_id === params.cohort_id);
      if (c) c.status = 'COMPLETED';
      return c;
    }

    case 'regenerate_enrollment_link': {
      const c = MOCK_COHORTS.find((c) => c.cohort_id === params.cohort_id);
      if (c) c.enrollment_link_code = `${Math.random().toString(36).slice(2, 10)}`;
      return c;
    }

    case 'list_all_students':
      return { students: MOCK_STUDENTS };

    case 'list_cohort_students':
      return { students: MOCK_STUDENTS.filter((s) => s.cohort_id === params.cohort_id) };

    case 'get_student':
      return MOCK_STUDENTS.find((s) => s.student_id === params.student_id) || null;

    case 'get_credit_status': {
      const s = MOCK_STUDENTS.find((s) => s.student_id === params.student_id);
      if (!s) return null;
      const cohort = MOCK_COHORTS.find((c) => c.cohort_id === s.cohort_id);
      const assessments = [];
      for (let i = 0; i < s.assessments_completed; i++) {
        const daysAgo = (s.assessments_completed - i) * 9;
        assessments.push({
          assessment_id: `assess-${s.student_id}-${i}`,
          client_name: i % 2 === 0 ? `${['Sarah M.', 'David K.', 'Linda P.', 'Michael T.', 'Jessica R.'][i % 5]}` : 'Acme Plumbing LLC',
          completed_at: new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString(),
          service_type: i % 3 === 0 ? 'business' : 'home',
          hours: i % 3 === 0 ? 2.5 : 1,
          mentor_name: MENTORS[i % MENTORS.length],
          status: 'completed',
        });
      }
      // Show the recent-settings-change banner only on active-cohort students
      // who have not yet qualified — they're the ones whose status could have
      // shifted from the change.
      const showRecentChange =
        cohort?.status === 'ACTIVE' &&
        s.status !== 'qualified' &&
        s.status !== 'withdrawn';
      return {
        student_id: s.student_id,
        student_name: s.name,
        student_email: s.email,
        cohort_id: s.cohort_id,
        cohort_name: s.cohort_name,
        course_code: s.course_code,
        credit_status: s.status,
        enrolled_at: s.enrolled_at,
        credit_start_date: s.enrolled_at,
        last_calculated_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        pla_credits: s.pla_credits,
        pla_notes: s.pla_credits ? 'Prior CompTIA Security+ certification — 15 hours applied' : null,
        requirements: cohort?.credit_requirements || {},
        progress: {
          hours_logged: s.hours_logged,
          assessments_completed: s.assessments_completed,
        },
        assessments,
        mentor_feedback_excerpt: s.assessments_completed > 0
          ? "Strong technical fundamentals. Recommended next focus: client communication during the on-site interview phase. Methodology adherence is excellent."
          : null,
        recent_settings_change: showRecentChange ? {
          date: RECENT_CHANGE.timestamp,
          changed_by: RECENT_CHANGE.actor_name,
          field: RECENT_CHANGE.field,
          field_label: RECENT_CHANGE.field_label,
          old_value: RECENT_CHANGE.old_value,
          new_value: RECENT_CHANGE.new_value,
        } : null,
      };
    }

    case 'set_pla_credits':
    case 'set_credit_start_date':
      return { success: true };

    case 'generate_transcript_pdf':
    case 'generate_cohort_summary_pdf':
    case 'generate_outcome_report_pdf':
      return { url: null, queued: true, message: 'Mock mode: PDF would be generated here' };

    case 'get_institution':
      return MOCK_INSTITUTION;

    case 'update_institution_settings': {
      // If credit_defaults changed, log the diff against previous values and
      // capture the supplied reason.
      if (params.credit_defaults) {
        const before = MOCK_INSTITUTION.credit_defaults || {};
        const after = params.credit_defaults;
        const reason = params.reason || 'No reason provided.';
        const FIELD_LABELS = {
          hours: 'Hours Required (default)',
          assessments: 'Assessments Required (default)',
        };
        Object.keys(after).forEach((key) => {
          if (before[key] !== after[key]) {
            MOCK_CHANGELOG.unshift({
              id: `chg-${Date.now()}-${key}`,
              timestamp: new Date().toISOString(),
              actor_name: MOCK_USER.name,
              actor_email: MOCK_USER.email,
              field: `${key}_required`,
              field_label: FIELD_LABELS[key] || key,
              old_value: before[key] ?? null,
              new_value: after[key],
              reason,
              affected_students: 22,
              became_at_risk: Math.max(0, after[key] - (before[key] ?? 0)),
            });
          }
        });
      }
      // Strip our internal `reason` before merging into the institution record.
      const { reason: _r, ...settingsToApply } = params;
      Object.assign(MOCK_INSTITUTION, settingsToApply);
      return MOCK_INSTITUTION;
    }

    case 'get_settings_changelog':
      return { entries: MOCK_CHANGELOG.slice(0, params.limit || 50) };

    case 'preview_settings_impact': {
      // params.credit_defaults = { hours, assessments }
      const proposed = params.credit_defaults || {};
      const current = MOCK_INSTITUTION.credit_defaults || {};
      const activeStudents = MOCK_STUDENTS.filter((s) => {
        const c = MOCK_COHORTS.find((c) => c.cohort_id === s.cohort_id);
        return c?.status === 'ACTIVE' && s.status !== 'withdrawn';
      });
      let mayBecomeAtRisk = 0;
      let nowQualified = 0;
      activeStudents.forEach((s) => {
        const wasOnTrack = s.status === 'on_track' || s.status === 'qualified';
        const newAssessmentsReq = proposed.assessments ?? current.assessments ?? 0;
        const newHoursReq = proposed.hours ?? current.hours ?? 0;
        const meetsAssess = s.assessments_completed >= newAssessmentsReq;
        const meetsHours = (s.hours_logged + (s.pla_credits || 0)) >= newHoursReq;
        const willQualify = meetsAssess && meetsHours;
        // Rough heuristic: previously on-track but now <50% of new requirement
        if (wasOnTrack && !willQualify) {
          const assessPct = s.assessments_completed / Math.max(1, newAssessmentsReq);
          if (assessPct < 0.5) mayBecomeAtRisk++;
        }
        if (s.status === 'at_risk' && willQualify) nowQualified++;
      });
      return {
        affected_students: activeStudents.length,
        may_become_at_risk: mayBecomeAtRisk,
        now_qualified: nowQualified,
        active_cohorts: MOCK_COHORTS.filter((c) => c.status === 'ACTIVE').length,
        diff: Object.keys(proposed).reduce((acc, key) => {
          if (current[key] !== proposed[key]) {
            acc[key] = { old: current[key], new: proposed[key] };
          }
          return acc;
        }, {}),
      };
    }

    case 'list_course_codes':
      return { course_codes: MOCK_COURSE_CODES };

    case 'set_course_code': {
      const existing = MOCK_COURSE_CODES.findIndex((c) => c.course_code === params.course_code);
      if (existing >= 0) MOCK_COURSE_CODES[existing] = { ...MOCK_COURSE_CODES[existing], ...params };
      else MOCK_COURSE_CODES.push({ ...params });
      return { success: true };
    }

    case 'delete_course_code': {
      const idx = MOCK_COURSE_CODES.findIndex((c) => c.course_code === params.course_code);
      if (idx >= 0) MOCK_COURSE_CODES.splice(idx, 1);
      return { success: true };
    }

    case 'list_observers':
      return { observers: MOCK_OBSERVERS };

    case 'invite_observer':
      MOCK_OBSERVERS.push({ email: params.email, name: params.email.split('@')[0], role: params.role, status: 'pending' });
      return { success: true };

    case 'revoke_observer': {
      const idx = MOCK_OBSERVERS.findIndex((o) => o.email === params.email);
      if (idx >= 0) MOCK_OBSERVERS.splice(idx, 1);
      return { success: true };
    }

    case 'get_insurance_upload_url':
      return { upload_url: 'mock://upload', public_url: 'mock://placeholder.pdf' };

    case 'set_insurance_certificate':
      MOCK_INSTITUTION.insurance_certificate = {
        url: params.public_url,
        uploaded_at: new Date().toISOString(),
        expires_at: null,
      };
      return { success: true };

    case 'get_billing_plan':
      return MOCK_BILLING_PLAN;

    case 'get_billing_period': {
      const period = getBillingPeriod();
      const activeStudents = MOCK_STUDENTS.filter((s) => {
        const c = MOCK_COHORTS.find((c) => c.cohort_id === s.cohort_id);
        return c?.status === 'ACTIVE' && s.status !== 'withdrawn';
      });
      const billable = activeStudents.length;
      const rate = MOCK_BILLING_PLAN.per_student_rate_cents;
      // The current period invoice is the most recent pending one.
      const currentInvoice = MOCK_INVOICES.find((i) => i.status === 'pending') || null;
      return {
        period_start: period.start_date,
        period_end: period.end_date,
        period_label: period.label,
        billable_students: billable,
        rate_cents: rate,
        amount_due_cents: billable * rate,
        status: currentInvoice?.status || 'paid',
        next_invoice_date: '2026-08-15',
        invoice_number: currentInvoice?.invoice_number || null,
        po_number: MOCK_BILLING_PLAN.payment_method.type === 'invoice'
          ? MOCK_BILLING_PLAN.payment_method.po_number : null,
      };
    }

    case 'list_invoices':
      return { invoices: MOCK_INVOICES };

    case 'get_billing_usage_breakdown': {
      const breakdown = MOCK_COHORTS.map((c) => {
        const studentsInCohort = MOCK_STUDENTS.filter((s) => s.cohort_id === c.cohort_id);
        const billable = studentsInCohort.filter((s) => s.status !== 'withdrawn').length;
        const assessmentsCompleted = studentsInCohort.reduce(
          (acc, s) => acc + (s.assessments_completed || 0), 0);
        return {
          cohort_id: c.cohort_id,
          cohort_name: c.name,
          status: c.status,
          billable_students: billable,
          enrolled_students: studentsInCohort.length,
          assessments_completed: assessmentsCompleted,
          cost_cents: billable * MOCK_BILLING_PLAN.per_student_rate_cents,
        };
      });
      return { breakdown, rate_cents: MOCK_BILLING_PLAN.per_student_rate_cents };
    }

    case 'get_payment_portal_url':
      return { url: 'https://billing.stripe.com/p/session/mock_portal_session' };

    case 'download_invoice_pdf': {
      const inv = MOCK_INVOICES.find((i) => i.invoice_id === params.invoice_id);
      return { url: inv?.pdf_url || null };
    }

    default:
      // eslint-disable-next-line no-console
      console.warn(`[mock] unhandled action: ${action}`, params);
      return { success: true };
  }
}

export const IS_MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';
